import { useCallback, useEffect, useState } from 'react';
import catalogService from '@/services/catalogService.ts';
import { invalidateCatalog } from '@/hooks/useCatalog.ts';
import type {
  CatalogEditDraft,
  CatalogEditCreateRequest,
  CatalogEditUpdateTranslationRequest,
  CatalogKind,
} from '@/types/catalog.ts';

export function useCreateCatalogDraft() {
  const [loading, setLoading] = useState(false);

  const create = useCallback(
    async (kind: CatalogKind, body: CatalogEditCreateRequest): Promise<CatalogEditDraft> => {
      setLoading(true);
      try {
        return await catalogService.createDraft(kind, body);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { create, loading };
}

export function useDraft(draftId: string | undefined, pollWhilePending = true) {
  const [draft, setDraft] = useState<CatalogEditDraft | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!draftId) return;
    setLoading(true);
    try {
      const d = await catalogService.getDraft(draftId);
      setDraft(d);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'DRAFT_FETCH_ERROR');
    } finally {
      setLoading(false);
    }
  }, [draftId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (!pollWhilePending || !draft) return;
    if (draft.status !== 'Pending' && draft.status !== 'Translating') return;
    const t = setInterval(fetch, 3000);
    return () => clearInterval(t);
  }, [draft, fetch, pollWhilePending]);

  return { draft, loading, error, refetch: fetch, setDraft };
}

export function useMyPendingDrafts() {
  const [drafts, setDrafts] = useState<CatalogEditDraft[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      setDrafts(await catalogService.getMyPendingDrafts());
    } catch {
      // silent fail — banner is auxiliary UI
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { drafts, loading, refetch: fetch };
}

export function useDraftActions() {
  return {
    confirm: async (draftId: string, kind: CatalogKind) => {
      const d = await catalogService.confirmDraft(draftId);
      invalidateCatalog(kind);
      return d;
    },
    discard: (draftId: string) => catalogService.discardDraft(draftId),
    retry: (draftId: string) => catalogService.retryDraft(draftId),
    updateTranslation: (draftId: string, body: CatalogEditUpdateTranslationRequest) =>
      catalogService.updateDraftTranslation(draftId, body),
  };
}
